/**
 * useSessionLifecycle Hook
 * 
 * Manages the complete session lifecycle:
 * - Loading scenario data (from ID or pre-loaded data)
 * - Starting the WebSocket session
 * - Ending the session and building results
 * - Session state management
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { EmotionType } from '../types/emotion';
import { useSessionServices, type ScenarioSessionData, type SessionResults } from '../context/SessionServicesContext';
import { getLocalizedCharacterName, MultilingualCharacterName } from '../utils/characterName';
import { logger } from '../utils/logger';
import { createIdentityAssertionTool } from '../config/identityTools';
import { assembleInstructions, type Language, type SessionMode } from '@wfvr/prompt-instructions';
import { TRANSCRIPTION_MODEL } from '@selna/session';

type SessionStatus = 'loading' | 'ready' | 'active' | 'finished';

interface PreLoadedScenarioData {
  id?: string;
  title?: string;
  isGameMode?: boolean;
  userReferenceInfo?: string;
  description?: string;
  objectives?: string[];
  prompt?: string;
  avatarImages?: Record<EmotionType, string>;
  category?: string;
  industry?: string;
  difficulty?: string;
  estimatedTime?: string;
  frontendSituation?: any;
  personaId?: string;
  voice?: string;
  characterName?: string | MultilingualCharacterName;
  [key: string]: any;
}

interface WebSocketSession {
  connect: (options?: { initialInputMode?: 'voice' | 'keyboard' | 'ptt'; allowKeyboardInput?: boolean }) => Promise<void>;
  disconnect: () => Promise<void>;
  updateSession: (config: any) => Promise<void>;
  triggerInitialGreeting: (delayMs?: number, avatarRole?: string, customTrigger?: string) => Promise<void>;
  cancelResponse: () => void; // 🎯 Cancel current AI response
  updateConfig: (config: any) => void;
  resumeAudioContext: () => Promise<void>; // 🔴 CHROME FIX: Resume AudioContext from user gesture
  resumeMicrophone: () => void; // Resume microphone input
  setInputMode: (mode: 'voice' | 'keyboard' | 'ptt', options?: { enableMicrophone?: boolean }) => void; // Set input mode
  state: {
    messages: Array<{ speaker: 'user' | 'ai'; text: string; timestamp: number }>;
  };
}

interface UseSessionLifecycleParams {
  scenarioId?: string;
  preLoadedScenarioData?: PreLoadedScenarioData;
  skipIntro?: boolean;
  initialInputMode?: 'voice' | 'keyboard' | 'ptt';
  allowKeyboardInput?: boolean;
  isDemoMode?: boolean;
  userId?: string;
  websocketSession: WebSocketSession;
  onFinish: (results: SessionResults) => void;
  onCancel?: () => void;
  /**
   * 🔒 Session tracking ID - CRITICAL for preventing race conditions
   * Pass this to ensure checkpoints are logged to the correct session
   */
  sessionTrackingId?: string;
  // Guided help statistics - function to get current stats when session ends
  getGuidedHelpStats?: () => {
    enabled: boolean;
    warningsShown: number;
    hintsShown: number;
    detailedHelpsUsed: number;
  } | undefined;
  // Callbacks to update parent state
  onWaitingForAIGreetingChange: (waiting: boolean) => void;
  onEmotionHistoryChange: (history: Array<{ timestamp: number; emotion: EmotionType }>) => void;
  // Save/Restore: If provided, this is a restored session with a previous conversation transcript
  initialTranscript?: Array<{ text: string; timestamp: number; speaker: 'user' | 'ai'; id?: string }>;
}

interface LoadingProgress {
  loaded: number;
  total: number;
}

interface UseSessionLifecycleReturn {
  sessionData: ScenarioSessionData | null;
  isLoading: boolean;
  loadingProgress: LoadingProgress | null;
  error: Error | null;
  sessionStatus: SessionStatus;
  sessionStartTime: Date | null;
  setSessionStartTime: (time: Date | null) => void; // Added for timer control
  emotionHistory: Array<{ timestamp: number; emotion: EmotionType }>;
  startSession: (initialInputMode?: 'voice' | 'keyboard' | 'ptt') => Promise<void>;
  endSession: () => Promise<void>;
  handleCancel: () => void;
  /** True while endSession is running — use as render guard to prevent flash of active UI */
  isEnding: boolean;
  /** True after user clicks Back/Cancel, before the component unmounts. */
  isCancelling: boolean;
  setError: (error: Error | null) => void;
  setSessionStatus: (status: SessionStatus) => void;
  setEmotionHistory: React.Dispatch<React.SetStateAction<Array<{ timestamp: number; emotion: EmotionType }>>>;
}

/**
 * Helper to determine the initial emotion based on scenario data.
 * Priority: behavioralProfile.initialEmotion > customPersona.initialEmotion > 'neutral'
 * Overridden to 'neutral' if positiveExpressionsOnly is active and emotion is not safe.
 */
function getInitialEmotion(scenarioData: any): EmotionType {
  let initialEmotion = (
    scenarioData?.behavioralProfile?.initialEmotion ||
    scenarioData?.customPersona?.initialEmotion ||
    'neutral'
  ) as EmotionType;

  const positiveExpressionsOnly = !!scenarioData?.positiveExpressionsOnly;
  if (positiveExpressionsOnly) {
    const allowedPositiveEmotions = ['neutral', 'interested', 'encouraging', 'proud', 'amused', 'relieved', 'enthusiastic', 'attentive', 'thinking', 'curious', 'contemplative', 'pensive'];
    if (!allowedPositiveEmotions.includes(initialEmotion)) {
      initialEmotion = 'neutral' as EmotionType;
    }
  }

  return initialEmotion;
}

export function useSessionLifecycle({
  scenarioId,
  preLoadedScenarioData,
  skipIntro = false,
  initialInputMode = 'ptt',
  allowKeyboardInput = true,
  isDemoMode = false,
  userId,
  websocketSession,
  onFinish,
  onCancel,
  sessionTrackingId,
  getGuidedHelpStats,
  onWaitingForAIGreetingChange,
  onEmotionHistoryChange,
  initialTranscript
}: UseSessionLifecycleParams): UseSessionLifecycleReturn {
  const services = useSessionServices();
  // NOTE: prompt assembly is now done via `assembleInstructions()` from
  // @wfvr/prompt-instructions (imported above). The services.combinePromptWithLanguage
  // wrapper still exists for backward compatibility but is intentionally not used here
  // — passing it through unconditionally was the root cause of the pt-BR TTS drift
  // observed in Game Mode (it appended 10 English instruction blocks to a PT prompt).
  const { createSession, preloadAvatarImages, validateScenarioData, logCheckpoint, getCurrentLanguage, config } = services;
  const { realtimeModel: REALTIME_MODEL, defaultVoice: DEFAULT_VOICE, analysisModeTools, conversationEndTool } = config;

  const [sessionData, setSessionData] = useState<ScenarioSessionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState<LoadingProgress | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>('loading');
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const [emotionHistory, setEmotionHistory] = useState<Array<{ timestamp: number; emotion: EmotionType }>>([]);

  // Prevent accidental retry storms from effects re-running across renders
  const hasAutoStartedRef = useRef(false);
  const startInProgressRef = useRef(false);

  // Sync emotion history with parent
  useEffect(() => {
    onEmotionHistoryChange(emotionHistory);
  }, [emotionHistory, onEmotionHistoryChange]);

  /**
   * Initialize with pre-loaded scenario data (deployment flow)
   */
  const initializeWithPreloadedData = useCallback(async () => {
    try {
      setIsLoading(true);
      logger.info('📋 Using pre-loaded scenario data');

      // The pre-loaded prompt is REQUIRED. No fallback: if it is missing the session
      // cannot start (no-fallbacks policy — silent defaults would mask the bug).
      const basePrompt = preLoadedScenarioData!.prompt;
      if (!basePrompt) {
        throw new Error(
          '[useSessionLifecycle] FATAL: preLoadedScenarioData.prompt is REQUIRED for initializeWithPreloadedData. ' +
          'The backend returned an empty prompt for this scenario.'
        );
      }
      logger.debug('📄 Pre-loaded prompt length:', basePrompt.length);

      const selectedLanguage = getCurrentLanguage();

      // `assembleInstructions()` is the single chokepoint that decides whether to
      // bolt on the soft-skills English instruction stack or pass the backend-built
      // prompt through untouched. For Game Mode the backend already produced a
      // complete, language-localized prompt via `buildGamePrompt()` — appending the
      // soft-skills English blocks would taint the TTS voice and trigger pt-BR drift.
      const mode: SessionMode =
        preLoadedScenarioData!.isGameMode === true ? 'game' : 'soft-skills';

      const promptWithLanguage = assembleInstructions(basePrompt, {
        mode,
        language: selectedLanguage as Language,
        languageMode: 'user-preference',
        characterName: preLoadedScenarioData!.characterName,
        includeAnalysisMode: true,
        includeDemoDuration: isDemoMode,
      });

      logger.debug('[LANG] useSessionLifecycle - Using language:', selectedLanguage);
      logger.debug('[LANG] Final prompt length with language:', promptWithLanguage.length, '(mode:', mode + ')');

      // Convert pre-loaded data to ScenarioSessionData format
      // IMPORTANT: Spread all scenario fields first, then override only what's needed with defaults
      // This ensures new fields (like behavioralProfile) automatically pass through
      const data: ScenarioSessionData = {
        success: true,
        sessionData: {
          instructions: promptWithLanguage,
          model: REALTIME_MODEL,
          voice: preLoadedScenarioData!.voice || DEFAULT_VOICE
        },
        scenario: {
          // Spread ALL fields from preLoadedScenarioData first
          ...(preLoadedScenarioData as any),
          // Then override with defaults only where needed
          id: preLoadedScenarioData!.id || 'unknown',
          title: preLoadedScenarioData!.title || 'Training Scenario',
          description: preLoadedScenarioData!.description || '',
          objectives: preLoadedScenarioData!.objectives || [],
          category: preLoadedScenarioData!.category || '',
          industry: preLoadedScenarioData!.industry || '',
          difficulty: preLoadedScenarioData!.difficulty || 'intermediate',
          estimatedTime: preLoadedScenarioData!.estimatedTime || '15 minutes',
          firstSpeaker: (preLoadedScenarioData as any).firstSpeaker || 'avatar',
          languageMode: (preLoadedScenarioData as any).languageMode || 'user-preference',
          // Explicit session mode flag (no ID heuristics)
          isGameMode: preLoadedScenarioData!.isGameMode === true,
          // Preserve original English aiRole for role reinforcement
          originalAiRole: (preLoadedScenarioData as any).originalAiRole || (preLoadedScenarioData as any).aiRole || undefined
        } as any,
        avatarImages: (preLoadedScenarioData!.avatarImages as Record<EmotionType, string>) || {},
        persona: null // TODO: Extract from preLoadedScenarioData if available
      };

      // Validate scenario data
      if (!data.sessionData.instructions) {
        throw new Error('Scenario prompt not found. Please generate prompt in admin first.');
      }

      // Separate images into critical (needed immediately) and background
      if (data.avatarImages && Object.keys(data.avatarImages).length > 0) {
        const initialEmotion = getInitialEmotion(data.scenario);
        const criticalImages: Record<string, string> = {};
        const backgroundImages: Record<string, string> = { ...data.avatarImages };
        
        // Extract critical images
        if (data.avatarImages[initialEmotion]) {
          criticalImages[initialEmotion] = data.avatarImages[initialEmotion];
          delete backgroundImages[initialEmotion];
        }
        if (data.avatarImages['neutral'] && initialEmotion !== 'neutral') {
          criticalImages['neutral'] = data.avatarImages['neutral'];
          delete backgroundImages['neutral'];
        }

        logger.info(`🖼️ Preloading ${Object.keys(criticalImages).length} critical avatar images...`);
        if (sessionTrackingId) {
          logCheckpoint('avatar_loading', sessionTrackingId);
        }
        
        // Await critical images for smooth UI transition
        await preloadAvatarImages(criticalImages, (loaded, total) => {
          setLoadingProgress({ loaded, total });
        });
        
        logger.info('✅ Critical images preloaded successfully, triggering background preload');
        
        // Fire-and-forget background load
        preloadAvatarImages(backgroundImages).catch(e => 
          logger.warn('Failed to background preload some images:', e)
        );
        
        if (sessionTrackingId) {
          logCheckpoint('avatar_loaded', sessionTrackingId);
        }
      }

      setSessionData(data);
      const initialStatus = skipIntro ? 'active' : 'ready';
      logger.info(`[INIT] Setting initial session status: ${initialStatus} (skipIntro=${skipIntro})`);
      setSessionStatus(initialStatus);
      setIsLoading(false);

      logger.info('✅ Scenario initialized with pre-loaded data:', data.scenario.title);

    } catch (err) {
      console.error('Failed to initialize with pre-loaded data:', err);
      setError(err as Error);
      setIsLoading(false);
    }
  }, [preLoadedScenarioData, skipIntro]);

  /**
   * Load scenario from backend (standalone flow)
   */
  const loadScenario = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      logger.info('📋 Loading scenario from backend:', scenarioId);

      // Load scenario configuration from backend
      const data = await createSession({
        scenarioId: scenarioId!,
        userId
      });

      // 🔍 CRITICAL DEBUG: Log what backend returns
      console.error('[useSessionLifecycle] 🔍 DATA FROM BACKEND:', {
        'aiRole': data.scenario.aiRole,
        'characterName': data.scenario.characterName,
        'userRole': data.scenario.userRole,
        'isGameMode': (data.scenario as any).isGameMode,
        'allKeys': Object.keys(data.scenario)
      });

      // Validate scenario data
      validateScenarioData(data);

      // 🎭 Save original aiRole BEFORE translation (for English role reinforcement)
      const originalAiRoleFromBackend = data.scenario.aiRole;
      
      // Apply translations to scenario data based on current language
      const currentLanguage = typeof window !== 'undefined' ? localStorage.getItem('selected_language') || 'en-US' : 'en-US';
      console.error('[useSessionLifecycle] 🌍 Current language:', currentLanguage);
      if (currentLanguage !== 'en-US' && data.scenario.translations) {
        console.error('[useSessionLifecycle] 🔍 BEFORE TRANSLATION - aiRole:', data.scenario.aiRole);
        if (services.getLocalizedScenario) {
          data.scenario = services.getLocalizedScenario(data.scenario, currentLanguage) as any;
        }
        console.error('[useSessionLifecycle] 🔍 AFTER TRANSLATION - aiRole:', data.scenario.aiRole);
        
        // 🎭 Restore original English aiRole for role reinforcement
        (data.scenario as any).originalAiRole = originalAiRoleFromBackend;
      } else {
        console.error('[useSessionLifecycle] ℹ️ No translation needed (en-US or no translations)');
        // Keep original as-is
        (data.scenario as any).originalAiRole = originalAiRoleFromBackend;
      }
      // Normalize explicit session mode flag (no ID heuristics).
      // Backend-created soft-skills scenarios default to false unless explicitly set.
      (data.scenario as any).isGameMode = (data.scenario as any).isGameMode === true;

      // MULTILINGUAL CHARACTER NAME SUPPORT
      // Replace {CHARACTER_NAME} and {CHARACTER_FIRST_NAME} placeholders with localized names
      if (data.scenario.characterName && (data.sessionData.instructions?.includes('{CHARACTER_NAME}') || data.sessionData.instructions?.includes('{CHARACTER_FIRST_NAME}'))) {
        const characterName = data.scenario.characterName;
        let localizedName: string;
        
        if (typeof characterName === 'string') {
          localizedName = characterName;
        } else {
          // Get language code (e.g., 'pt-PT' -> 'pt')
          let langCode = currentLanguage.split('-')[0];
          // Handle Danish: language code is 'da' but object key is 'dk'
          if (langCode === 'da') langCode = 'dk';
          localizedName = characterName[langCode as keyof typeof characterName] || characterName.en || 'Character';
        }
        
        const localizedFirstName = localizedName.split(' ')[0];
        
        const fullNameCount = (data.sessionData.instructions.match(/{CHARACTER_NAME}/g) || []).length;
        const firstNameCount = (data.sessionData.instructions.match(/{CHARACTER_FIRST_NAME}/g) || []).length;
        
        data.sessionData.instructions = data.sessionData.instructions.replace(/{CHARACTER_NAME}/g, localizedName);
        data.sessionData.instructions = data.sessionData.instructions.replace(/{CHARACTER_FIRST_NAME}/g, localizedFirstName);
        
        logger.debug(`🔄 [CHARACTER_NAME] Replaced ${fullNameCount} full name + ${firstNameCount} first name occurrences for language: ${currentLanguage}`);
      }

      // Separate images into critical (needed immediately) and background
      if (data.avatarImages && Object.keys(data.avatarImages).length > 0) {
        const initialEmotion = getInitialEmotion(data.scenario);
        const criticalImages: Record<string, string> = {};
        const backgroundImages: Record<string, string> = { ...data.avatarImages };
        
        // Extract critical images
        if (data.avatarImages[initialEmotion]) {
          criticalImages[initialEmotion] = data.avatarImages[initialEmotion];
          delete backgroundImages[initialEmotion];
        }
        if (data.avatarImages['neutral'] && initialEmotion !== 'neutral') {
          criticalImages['neutral'] = data.avatarImages['neutral'];
          delete backgroundImages['neutral'];
        }

        logger.info(`🖼️ Preloading ${Object.keys(criticalImages).length} critical avatar images...`);
        // 🔒 sessionTrackingId is REQUIRED - fail fast if missing
        if (sessionTrackingId) {
          logCheckpoint('avatar_loading', sessionTrackingId);
        }
        
        // Await critical images for smooth UI transition
        await preloadAvatarImages(criticalImages, (loaded, total) => {
          setLoadingProgress({ loaded, total });
        });
        
        logger.info('✅ Critical images preloaded successfully, triggering background preload');
        
        // Fire-and-forget background load
        preloadAvatarImages(backgroundImages).catch(e => 
          logger.warn('Failed to background preload some images:', e)
        );
        
        if (sessionTrackingId) {
          logCheckpoint('avatar_loaded', sessionTrackingId);
        }
      }

      setSessionData(data);
      logger.info('[INIT] Setting session status: ready (no skipIntro in normal load)');
      setSessionStatus('ready');

      logger.info('✅ Scenario loaded successfully:', data.scenario.title);

    } catch (err) {
      console.error('Failed to load scenario:', err);
      setError(err as Error);
      setSessionStatus('loading'); // Stay in loading to show error
    } finally {
      setIsLoading(false);
    }
  }, [scenarioId, userId]);

  /**
   * Start the session - connect WebSocket and configure AI
   */
  const startSession = useCallback(async (initialInputMode: 'voice' | 'keyboard' | 'ptt' = 'ptt') => {
    if (startInProgressRef.current) {
      console.warn('⚠️ startSession ignored - already in progress');
      return;
    }
    startInProgressRef.current = true;
    logger.debug('═══════════════════════════════════════════');
    logger.info('🚀 START SESSION CALLED');
    logger.debug('sessionData:', !!sessionData);
    logger.info('⌨️ Initial input mode:', initialInputMode);
    logger.debug('═══════════════════════════════════════════');

    if (!sessionData) {
      console.warn('⚠️ Cannot start session - no sessionData');
      return;
    }

    try {
      // 🔴 CHROME FIX: Resume AudioContext IMMEDIATELY from user gesture
      // CRITICAL: This MUST be the first async operation to satisfy Chrome's autoplay policy
      // If we do any other async work first, Chrome considers it "not a user gesture"
      logger.info('[🔴 CHROME] 🎵 Resuming AudioContext from user click (before any async ops)...');
      await websocketSession.resumeAudioContext();
      logger.info('[🔴 CHROME] ✅ AudioContext resume completed');

      // 🎤 IMMEDIATELY show overlay - before anything else
      logger.debug('🎤 Calling onWaitingForAIGreetingChange(true)...');
      onWaitingForAIGreetingChange(true);
      logger.debug('🎤 onWaitingForAIGreetingChange(true) called - overlay should be shown');

      logger.info('🚀 Starting WebSocket connection...');
      logger.debug('📋 Session config:', {
        model: sessionData.sessionData.model,
        voice: sessionData.sessionData.voice,
        hasInstructions: !!sessionData.sessionData.instructions
      });

      // NOTE: Session start time is now set when overlay disappears (in ScenarioSessionComponent)
      // This ensures timer only starts when conversation actually begins
      logger.info('⏱️ Session activated - timer will start when overlay disappears');

      // 🎭 Initialize emotion history with initial emotion from scenario
      // Uses the same getInitialEmotion helper logic we use for critical image preloading
      const initialEmotion = getInitialEmotion(sessionData.scenario);

      logger.debug('🎭 Initial emotion:', initialEmotion, {
        fromBehavioralProfile: sessionData.scenario.behavioralProfile?.initialEmotion,
        fromCustomPersona: sessionData.scenario.customPersona?.initialEmotion,
        positiveExpressionsOnly: !!(sessionData.scenario as any).positiveExpressionsOnly,
        used: initialEmotion
      });

      setEmotionHistory([{
        timestamp: Date.now(), // Use current time since session hasn't officially started yet
        emotion: initialEmotion
      }]);

      // 💾 Determine if this is a restored session (used for prompt injection and greeting)
      const isRestoredSession = initialTranscript && initialTranscript.length > 0;

      // 🚨 CRITICAL: Set instructions BEFORE connect() to prevent empty prompt error
      if (sessionData.sessionData.instructions) {
        const selectedLanguage = getCurrentLanguage();
        const languageMode = sessionData.scenario.languageMode || 'user-preference';
        const isGameSession = (sessionData.scenario as any).isGameMode === true;
        const sessionTemperature = isGameSession ? 0.8 : 0.7;

        // Check if Analysis Mode is enabled for this scenario (default: true)
        // When false, the trigger instruction is not included and the tool is not available
        const analysisModeEnabled = (sessionData.scenario as any).analysisModeEnabled !== false;
        logger.debug('[useSessionLifecycle] 🔬 Analysis Mode enabled:', analysisModeEnabled);
        logger.debug('[useSessionLifecycle] 🌡️ Session temperature:', {
          sessionType: isGameSession ? 'game' : 'soft-skills',
          temperature: sessionTemperature,
        });

        // `assembleInstructions()` is the single chokepoint that decides whether to
        // bolt on the soft-skills English instruction stack or pass the prompt through
        // untouched. For Game Mode the prompt arriving here was already produced
        // complete by the backend's `buildGamePrompt()`; appending the soft-skills
        // English blocks here is what was causing the TTS to drift to pt-BR.
        const mode: SessionMode = isGameSession ? 'game' : 'soft-skills';

        let finalInstructions = assembleInstructions(
          sessionData.sessionData.instructions,
          {
            mode,
            language: selectedLanguage as Language,
            languageMode: languageMode as 'user-preference' | 'english-only',
            characterName: sessionData.scenario.characterName,
            includeAnalysisMode: analysisModeEnabled,
            includeDemoDuration: isDemoMode,
          }
        );

        logger.debug('[useSessionLifecycle] 🌍 Final instructions assembled:', { selectedLanguage, languageMode, isDemoMode, mode });

        // 💾 RESTORE: If this is a restored session, inject conversation context into the system prompt
        // This ensures the AI has full context BEFORE the connection and greeting,
        // so it can naturally acknowledge the pause and resume the conversation
        if (isRestoredSession) {
          // Game runs pt-PT — the resume block must not introduce English into the
          // prompt (TTS would drift to pt-BR). Soft-skills keeps the English stack.
          const isGame = mode === 'game';
          const conversationContext = initialTranscript
            .filter(m => m.text)
            .map(m => `[${m.speaker === 'ai' ? (isGame ? 'Tu' : 'You') : (isGame ? 'A outra pessoa' : 'The user')}]: ${m.text}`)
            .join('\n');

          if (conversationContext) {
            finalInstructions += isGame
              ? '\n\n' +
                '--- CONVERSA RETOMADA ---\n' +
                'Esta conversa foi interrompida e está a ser retomada agora. Aqui está o que já se passou:\n\n' +
                conversationContext + '\n\n' +
                'Retomas em personagem: reconheces a pausa com naturalidade, pegas em algo concreto do que já foi dito e continuas de onde ficaste, sem recomeçar nem repetir o cumprimento inicial.\n' +
                '--- FIM ---'
              : '\n\n' +
                '--- RESUMED SESSION ---\n' +
                'This is a RESUMED conversation. The user paused the session and is now coming back.\n' +
                'Here is the previous conversation that already happened:\n\n' +
                conversationContext + '\n\n' +
                'CRITICAL INSTRUCTIONS FOR RESUMING:\n' +
                '- REMEMBER YOUR ROLE. You MUST not swap roles with the user.\n' +
                '- If you were the one with a problem before, you still are. If the user was helping you, they still are.\n' +
                '- You MUST acknowledge that the conversation was paused and welcome the user back IN CHARACTER\n' +
                '- Reference something specific from the previous conversation to show you remember\n' +
                '- Then naturally continue from where you left off\n' +
                '- Do NOT start from scratch or repeat your initial greeting\n' +
                '- Do NOT summarize the entire conversation - just show you remember and pick up naturally\n' +
                '--- END RESUMED SESSION ---';

            logger.info('[💾 RESTORE] Injected conversation context into system prompt (' + initialTranscript.length + ' messages, ' + (isGame ? 'pt-PT' : 'en') + ')');
          }
        }
        
        // Send session configuration BEFORE connect() so instructions are available
        const voiceToUse = sessionData.sessionData.voice || DEFAULT_VOICE;
        logger.debug('[useSessionLifecycle] 🎙️ Calling updateSession with voice:', voiceToUse, '(from sessionData.sessionData.voice:', sessionData.sessionData.voice, ')');
        
        // Build tools array
        // Identity assertion tool is ALWAYS included (prevents role confusion)
        // Use enum-constrained version when roles are known for deterministic validation
        const scenarioAiRole = (sessionData.scenario as any)?.aiRole;
        const scenarioUserRole = (sessionData.scenario as any)?.userRole;
        // No fallback (AI_INSTRUCTIONS): the identity tool's role+language enums anchor
        // against dialect drift; if the scenario lacks the roles, fail loud rather than
        // degrade to a free-text tool that lets Brazilian PT through.
        if (!scenarioAiRole || !scenarioUserRole) {
          throw new Error('[useSessionLifecycle] FATAL: scenario.aiRole and scenario.userRole are REQUIRED to build the identity assertion tool.');
        }
        const identityTool = createIdentityAssertionTool(scenarioAiRole, scenarioUserRole, selectedLanguage);
        const tools = [identityTool];
        // Always include analysis tools if they exist (they might contain other global tools like change_language)
        // If analysis mode is disabled, we should ideally filter out specific tools, but for now we'll include them
        // since the instructions control whether the AI will actually use them
        if (analysisModeTools) { 
          tools.push(...analysisModeTools); 
        }
        if (isDemoMode) {
          if (conversationEndTool) { tools.push(conversationEndTool); }
        }

        // GA API session.update.
        // For the full Beta→GA field map, see packages/session/src/services/realtime/RealtimeAPITypes.ts.
        //
        // We explicitly send audio.input.format and audio.output.format. The Beta API used
        // input_audio_format/output_audio_format as strings; in GA those moved into the audio
        // subtree as objects. Leaving them out makes the server fall back to a default
        // negotiation that does not always match our pipeline (PCM16 mono @ 24 kHz).
        await websocketSession.updateSession({
          type: 'realtime',
          model: REALTIME_MODEL,
          instructions: finalInstructions,
          output_modalities: ['audio'],
          audio: {
            input: {
              format: { type: 'audio/pcm', rate: 24000 },
              transcription: {
                model: TRANSCRIPTION_MODEL,
                // No language parameter - let OpenAI auto-detect for ALL languages equally
              },
              // Turn detection starts null (disabled) — enabled after greeting finishes
              turn_detection: null,
            },
            output: {
              voice: voiceToUse,
              format: { type: 'audio/pcm', rate: 24000 },
            },
          },
          // tool_choice: 'required' forces the model to call a tool before every
          // response. The model picks freely between available tools (assert_identity,
          // enter_analysis_mode, change_language). In practice it chooses assert_identity
          // most of the time because the prompt instructs it to. The forced-function
          // form {type:'function',name:'...'} would make this deterministic, but it
          // caused gpt-realtime-1.5 to fallback to text output instead of audio — so
          // it stays 'required'. Residual PT-BR drift on the rare turns where the model
          // skips assert_identity is a known limitation of 'required'.
          //
          // Follow-up: after assert_identity, sendFunctionResult passes
          // responseToolChoice='auto' (see ScenarioSessionInner). That follow-up
          // response.create carries an explicit override so it is NOT forced into
          // another tool call (no infinite loop), while still letting the model act on
          // the user's turn (enter_analysis_mode / change_language) instead of being
          // boxed into speech-only by a blunt 'none'.
          ...(tools.length > 0 ? {
            tools,
            tool_choice: 'required',
          } : {
            tools: [],
            tool_choice: 'none'
          }),
        });

      }

      // Connect to WebSocket with scenario configuration
      // 🚨 CRITICAL: This must be AFTER updateSession() so instructions are available
      logger.info('🔌 Calling websocketSession.connect()...');
      // 🔒 sessionTrackingId is REQUIRED - fail fast if missing
      if (sessionTrackingId) {
        logCheckpoint('webrtc_connecting', sessionTrackingId);
      }
      await websocketSession.connect({ initialInputMode, allowKeyboardInput });
      logger.info('✅ WebSocket connected successfully');
      if (sessionTrackingId) {
        logCheckpoint('webrtc_connected', sessionTrackingId);
      }

      // ⌨️ Set initial input mode AFTER connect AND overlay is shown (so microphone exists and user sees blocking overlay)
      // CRITICAL: Must be after connect() so mic can be properly paused/resumed
      // CRITICAL: Must be after overlay is shown to prevent 1-2s window where mic is active without visual feedback
      // 🎯 FIX: Don't enable microphone immediately - wait for overlay to disappear
      logger.info('⌨️ Setting initial input mode to:', initialInputMode);
      websocketSession.setInputMode(initialInputMode, { enableMicrophone: false });

      // 🎬 AUTO-GREETING: Trigger character to speak first (or not, depending on firstSpeaker setting)
      const firstSpeaker = sessionData.scenario.firstSpeaker || 'avatar'; // Default to avatar for backwards compatibility
      logger.info(`🎬 First speaker setting: ${firstSpeaker}`);
      
      if (firstSpeaker === 'avatar') {
        // Trigger greeting with character role (passed directly, not via config)
        const characterNameValue = sessionData.scenario.characterName;
        const localizedCharacterName = characterNameValue ? getLocalizedCharacterName(characterNameValue, getCurrentLanguage()) : undefined;
        const avatarRole = sessionData.persona?.role || localizedCharacterName || 'character';

        if (isRestoredSession) {
          // 💾 RESTORED SESSION: Use a special greeting trigger that acknowledges the pause
          logger.info('🎬💾 Triggering RESTORED greeting (acknowledging pause)...');
          // Note: characterRole is passed directly to triggerInitialGreeting, not via updateConfig
          // to avoid unnecessary warnings about runtime config updates
          await websocketSession.triggerInitialGreeting(undefined, avatarRole, 
            'SYSTEM: The user has returned after pausing the conversation. ' +
            'Ensure you continue playing your role (' + avatarRole + '). REMEMBER YOUR ROLE. You MUST not swap roles with the user. ' +
            'If you were the one with a problem before, you still are. If the user was helping you, they still are. ' +
            'Welcome them back warmly, returning seamlessly to your character. Briefly reference something from your previous conversation ' +
            'to show you remember, and naturally continue from where you left off. ' +
            'Keep it short and natural - do NOT summarize everything that was said before.'
          );
          logger.info('✅ Restored greeting triggered - character should welcome user back');
        } else {
          // Normal first-time greeting
          logger.info('🎬 Triggering character to speak first (immediately)...');
          // Note: characterRole is passed directly to triggerInitialGreeting, not via updateConfig
          // to avoid unnecessary warnings about runtime config updates
          await websocketSession.triggerInitialGreeting(undefined, avatarRole);
          logger.info('✅ Auto-greeting triggered - character should greet user now');
        }
        // turn_detection will be enabled when greeting finishes (handled in useAIGreeting callback)
      } else {
        logger.info('👤 User will speak first - not triggering initial greeting');
        
        // ⚠️ CRITICAL: Set turn_detection based on input mode
        if (initialInputMode === 'voice') {
          // VAD mode: enable semantic_vad for auto speech detection
          logger.info('📡 Enabling turn_detection (semantic_vad) for user-first VAD scenario...');
          // 🚨 GA API: turn_detection lives inside audio.input (not at session top level)
          await websocketSession.updateSession({
            type: 'realtime',
            audio: {
              input: {
                turn_detection: {
                  type: "semantic_vad",
                  eagerness: "high"
                }
              }
            }
          });
          logger.info('✅ turn_detection enabled (semantic_vad)');
        } else if (initialInputMode === 'ptt') {
          // PTT mode: keep turn_detection null (user controls start/stop)
          logger.info('🎙️ PTT mode - turn_detection stays null (manual control)');
        }
        // keyboard mode: turn_detection stays null (no mic)
        
        // Enable microphone immediately since user needs to speak first (only in voice/ptt modes)
        if (initialInputMode === 'voice') {
          logger.info('🎤 Enabling microphone for user to speak first (voice mode)');
          websocketSession.resumeMicrophone();
        } else if (initialInputMode === 'ptt') {
          // 🔧 FIX: Arm PTT mode (setPTTMode + startRecording + disable turn_detection).
          // Without this, isPTTModeActive() returns false and handlePTTPressed bails out
          // silently — the button vibrates but no audio is captured or streamed.
          // The greeting-finished callback (onEnableMicrophoneAfterWarmup) does this
          // for the avatar-first path, but the user-first path was missing it.
          logger.info('🎙️ PTT mode - arming microphone + PTT mode for user-first speech');
          await websocketSession.setInputMode('ptt', { enableMicrophone: true });
        } else {
          logger.info('⌨️ Keyboard mode - microphone stays paused');
        }
        
        // CRITICAL: Hide overlay after short delay so user knows they can speak/type
        // We show overlay briefly to confirm session is ready, then hide it as cue to start
        logger.debug('⏱️ Scheduling overlay hide in 1.5 seconds...');
        setTimeout(() => {
          logger.debug('🎤 Hiding overlay - user can now speak/type');
          onWaitingForAIGreetingChange(false);
        }, 1500); // 1.5 seconds to show "You will speak first..." then disappear
      }

      logger.info('✅ Session started successfully');

    } catch (err) {
      console.error('❌ Failed to start session:', err);
      console.error('Error details:', err);
      setError(err as Error);
      setSessionStatus('loading'); // Go back to error state
      // Disconnect to clean up
      await websocketSession.disconnect();
    } finally {
      startInProgressRef.current = false;
    }
  }, [sessionData, websocketSession, onWaitingForAIGreetingChange, initialInputMode, allowKeyboardInput, initialTranscript]);

  /**
   * End the session - disconnect and build results
   */
  const [isEnding, setIsEnding] = useState(false);
  const endSession = useCallback(async () => {
    if (isEnding) {
      logger.debug('[endSession] Already ending - ignoring duplicate call');
      return;
    }
    if (!sessionData || !sessionStartTime) return;
    setIsEnding(true);

    try {
      logger.info('🏁 Ending session...');

      // Prevent ConnectionLostOverlay from flashing during intentional disconnect
      // Same pattern as handleCancel (Back button)
      isCancellingRef.current = true;

      const endTime = new Date();
      const duration = Math.floor((endTime.getTime() - sessionStartTime.getTime()) / 1000);

      // Build transcript from timestamped messages
      // CRITICAL: Capture messages BEFORE disconnect to avoid state changes during async operation
      const transcript = websocketSession.state.messages.map(msg => ({
        timestamp: msg.timestamp,
        speaker: msg.speaker,
        text: msg.text
      }));

      // Get guided help stats if available
      const guidedHelpStats = getGuidedHelpStats ? getGuidedHelpStats() : undefined;

      logger.debug('📊 Session summary:', {
        duration: `${duration}s`,
        messageCount: transcript.length,
        emotionChanges: emotionHistory.length,
        guidedHelpStats,
        startTime: sessionStartTime.toISOString(),
        endTime: endTime.toISOString()
      });

      // Build session results
      // CRITICAL: Use the scenarioId parameter that was explicitly passed, not sessionData.scenario.id
      // This ensures the correct scenario is tracked even after translation/localization
      const actualScenarioId = scenarioId || sessionData.scenario.id;
      
      const results: SessionResults = {
        sessionId: `session_${Date.now()}`,
        scenarioId: actualScenarioId,
        userId: userId || 'anonymous',
        tenantId: 'default',

        startedAt: sessionStartTime,
        endedAt: endTime,
        duration,
        status: 'completed',

        transcript,
        emotionHistory,
        guidedHelpStats,

        scenario: {
          id: actualScenarioId,
          title: sessionData.scenario.title,
          category: sessionData.scenario.category || ''
        },

        persona: sessionData.persona
      };

      logger.info('✅ Session ended - navigating to results immediately');

      // 🚀 CRITICAL: Navigate to results IMMEDIATELY (shows FeedbackLoadingState)
      // Don't wait for disconnect - it causes flashing as WebSocket states change during async operation
      // The parent will call navigate() which unmounts this component, cleaning up naturally
      onFinish(results);

      // Disconnect WebSocket in background (fire-and-forget)
      // Component will likely be unmounted by the time this completes
      websocketSession.disconnect().catch(err => {
        console.warn('WebSocket disconnect error (non-critical):', err);
      });

    } catch (err) {
      console.error('Failed to end session:', err);
      setError(err as Error);
    }
  }, [sessionData, sessionStartTime, emotionHistory, getGuidedHelpStats, userId, websocketSession, onFinish]);

  /**
   * Cancel the session.
   *
   * We set isCancelling=true *before* disconnecting so that the
   * ConnectionLostOverlay condition can skip rendering during an
   * intentional user-initiated back/cancel (prevents the "connection lost"
   * flash that otherwise appears while React processes the disconnect
   * state change).
   */
  const isCancellingRef = useRef(false);

  const handleCancel = useCallback(() => {
    isCancellingRef.current = true;

    // Disconnect if connected
    if (websocketSession) {
      websocketSession.disconnect();
    }

    onCancel?.();
  }, [websocketSession, onCancel]);

  // Load scenario on mount (if not pre-loaded)
  useEffect(() => {
    if (preLoadedScenarioData) {
      initializeWithPreloadedData();
    } else if (scenarioId) {
      loadScenario();
    } else {
      setError(new Error('Either scenarioId or scenarioData must be provided'));
      setIsLoading(false);
    }
  }, [scenarioId, preLoadedScenarioData, initializeWithPreloadedData, loadScenario]);

  // Note: WebSocket config (model, voice) is set via updateSession() when session starts,
  // not via updateConfig() which requires reconnection. Removing unnecessary updateConfig calls.

  // Auto-start session when skipIntro is true and data is ready
  useEffect(() => {
    // Skip auto-start if not configured
    
    if (!skipIntro) return;
    if (error) return;
    if (hasAutoStartedRef.current) return;
    if (sessionStatus === 'active' && sessionData && !sessionStartTime) {
      hasAutoStartedRef.current = true;
      logger.info('⏩ Auto-starting WebSocket session (skipIntro mode) with input mode:', initialInputMode);
      startSession(initialInputMode);
    }
  }, [sessionStatus, sessionData, sessionStartTime, skipIntro, initialInputMode, startSession, error]);

  return {
    sessionData,
    isLoading,
    loadingProgress,
    error,
    sessionStatus,
    sessionStartTime,
    setSessionStartTime, // Added for timer control
    emotionHistory,
    startSession,
    endSession,
    handleCancel,
    /** True while endSession is running — use as render guard to prevent flash of active UI */
    isEnding,
    /** True after user clicks Back/Cancel, before the component unmounts. */
    isCancelling: isCancellingRef.current,
    setError,
    setSessionStatus,
    setEmotionHistory
  };
}

